import React, { useCallback, useEffect, useState } from "react";
import { Bell, CheckCheck, Receipt, Clock, ListOrdered, IdCard, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Spinner, EmptyState } from "../../../components/common";
import { Button } from "../../../components/ui/button";
import { api, errMsg, fmtDateTime } from "../../../lib/api";

/* Phase 201 — parking alerts feed (expiry reminders, receipts, waitlist slot, pass renewal, overstay). SMS / WhatsApp /
   push copies go out when the Super Admin has configured those integrations; in-app always works. */

const ICONS = { parking_receipt: Receipt, parking_expiry: Clock, parking_waitlist_slot: ListOrdered, parking_pass_renewal: IdCard, parking_overstay: AlertTriangle, parking_no_show: AlertTriangle };

export function AlertsTab({ ctx }) {
  const { code, live, refresh } = ctx;
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get(`/t/${code}/parking/citizen/notifications`).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))), [code]);
  useEffect(() => { load(); }, [load, live]);
  const readAll = async () => { try { await api.post(`/t/${code}/parking/citizen/notifications/read-all`); load(); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  if (!rows) return <Spinner />;
  const unread = rows.filter((r) => !r.read_at).length;
  return (
    <div className="space-y-3" data-testid="citizen-parking-alerts">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">{unread ? `${unread} unread` : "All caught up"} · reminders before your time ends, receipts, waitlist and pass renewals.</p>
        {unread > 0 && <Button size="sm" variant="outline" onClick={readAll} data-testid="alerts-read-all"><CheckCheck className="h-4 w-4 mr-1.5" /> Mark all read</Button>}
      </div>
      {rows.length === 0 ? <EmptyState title="No parking alerts yet" subtitle="You will see booking receipts and expiry reminders here." /> : (
        <div className="space-y-2">
          {rows.map((n) => {
            const Icon = ICONS[n.type] || Bell;
            return (
              <div key={n.id} className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 ${n.read_at ? "" : "bg-primary/5 border-primary/30"}`} data-testid={`alert-row-${n.id}`}>
                <span className="h-8 w-8 rounded-full bg-accent inline-flex items-center justify-center shrink-0 mt-0.5"><Icon className="h-4 w-4 text-accent-foreground" /></span>
                <div className="min-w-0 flex-1"><p className="text-sm font-medium">{n.title}</p>{(n.content || n.body) && <p className="text-xs text-muted-foreground mt-0.5">{n.content || n.body}</p>}<p className="text-[11px] text-muted-foreground mt-1">{fmtDateTime(n.created_at)}</p></div>
                {!n.read_at && <span className="h-2 w-2 rounded-full bg-primary mt-2 shrink-0" aria-label="Unread" />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
